import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { DataGrid } from '@/components/data-grid/core/DataGrid';
import { Check, Eye, RotateCcw, Ban } from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import {
  HarborClubRequest as HarborAssociationRequest,
  useGetHarborClubRequestsQuery,
  useApproveHarborClubRequestMutation,
  useRejectHarborClubRequestMutation,
  useRevokeHarborClubRequestMutation,
  useCancelHarborAssociationMutation,
} from '@/services/HarborAssociationService';
import HarborClubRequestDetailsModal from './HarborClubRequestDetailsModal';
import { KeenIcon } from '@/components';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDisplayDate } from '@/utils/Date';

const HarborClubRequests: React.FC = () => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const [statusFilter, setStatusFilter] = useState('');

  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] =
    useState<HarborAssociationRequest | null>(null);

  const harborId = useSelector(
    (state: RootState) => state.auth.selectedHarborId
  );

  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();

  const [approveRequest, { isLoading: isApproving }] =
    useApproveHarborClubRequestMutation();
  const [rejectRequest, { isLoading: isRejecting }] =
    useRejectHarborClubRequestMutation();
  const [revokeRequest, { isLoading: isRevoking }] =
    useRevokeHarborClubRequestMutation();
  const [cancelRequest, { isLoading: isCancelling }] =
    useCancelHarborAssociationMutation();

  const queryParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedSearchTerm || undefined,
    status: statusFilter || undefined,
    harbor_id: harborId || undefined,
  };

  const {
    data: requestsData,
    isLoading: isRequestsLoading,
    error: requestsError,
    refetch,
    isFetching,
  } = useGetHarborClubRequestsQuery(queryParams, {
    refetchOnMountOrArgChange: true,
    skip: !harborId,
  });

  const processedData = React.useMemo(() => {
    if (!requestsData?.data) return [];

    const dataArray = [...requestsData.data];
    return dataArray;
  }, [requestsData]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);

    setPagination({
      pageIndex: 0,
      pageSize: pagination.pageSize,
    });

    const timer = setTimeout(() => {
      setDebouncedSearchTerm(e.target.value);
    }, 500);

    return () => clearTimeout(timer);
  };

  const handleStatusFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setStatusFilter(e.target.value);

    setPagination({
      pageIndex: 0,
      pageSize: pagination.pageSize,
    });
  };

  useEffect(() => {
    if (requestsData) {
      setTotalPages(requestsData.totalPages || 1);
      setTotalItems(requestsData.totalItems || 0);
    }
  }, [requestsData]);

  const handleApproveRequest = async (id: string, message?: string) => {
    try {
      await approveRequest({
        request_id: id,
        response_message: message,
      }).unwrap();
      enqueueSnackbar('Club request approved successfully', {
        variant: 'success',
      });
      refetch();
      setIsDetailsModalOpen(false);
    } catch (error) {
      console.error('Error approving club request:', error);
      enqueueSnackbar('Failed to approve club request', { variant: 'error' });
    }
  };

  const handleRejectRequest = async (id: string, message?: string) => {
    try {
      await rejectRequest({
        request_id: id,
        response_message: message,
      }).unwrap();
      enqueueSnackbar('Club request rejected successfully', {
        variant: 'success',
      });
      refetch();
      setIsDetailsModalOpen(false);
    } catch (error) {
      console.error('Error rejecting club request:', error);
      enqueueSnackbar('Failed to reject club request', { variant: 'error' });
    }
  };

  const handleRevokeRequest = async (id: string, reason?: string) => {
    try {
      await revokeRequest({
        request_id: id,
        reason,
      }).unwrap();
      enqueueSnackbar('Association revoked successfully', {
        variant: 'success',
      });
      refetch();
      setIsDetailsModalOpen(false);
    } catch (error) {
      console.error('Error revoking association:', error);
      enqueueSnackbar('Failed to revoke association', { variant: 'error' });
    }
  };

  const handleCancelRequest = async (
    id: string,
    action: 'withdraw' | 'reject',
    reason?: string
  ) => {
    try {
      await cancelRequest({
        request_id: id,
        action,
        reason,
      }).unwrap();
      enqueueSnackbar(
        `Request ${action === 'withdraw' ? 'withdrawn' : 'cancelled'} successfully`,
        {
          variant: 'success',
        }
      );
      refetch();
      setIsDetailsModalOpen(false);
    } catch (error) {
      console.error(`Error ${action}ing request:`, error);
      enqueueSnackbar(`Failed to ${action} request`, { variant: 'error' });
    }
  };

  const handleOpenDetailsModal = (request: HarborAssociationRequest) => {
    setSelectedRequest(request);
    setIsDetailsModalOpen(true);
  };

  useEffect(() => {
    if (requestsError) {
      console.error('Error fetching club requests:', requestsError);
      enqueueSnackbar('Failed to load club requests', { variant: 'error' });
    }
  }, [requestsError, enqueueSnackbar]);

  const columns = React.useMemo(
    () => [
      {
        accessor: (row: HarborAssociationRequest) => row.club?.name,
        id: 'club',
        header: 'Club',
        enableSorting: false,
        cell: ({ row }: { row: { original: HarborAssociationRequest } }) => (
          <div className="flex items-center">
            {row.original.club && row.original.club.logo && (
              <img
                src={row.original.club.logo}
                alt={`${row.original.club.name} logo`}
                className="w-8 h-8 rounded-full mr-2 object-cover"
              />
            )}
            <span>{row.original.club?.name || 'Unknown Club'}</span>
          </div>
        ),
      },
      {
        accessor: (row: HarborAssociationRequest) => row.status,
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: HarborAssociationRequest } }) => {
          const value = row.original.status;
          if (!value) return <span>Unknown</span>;

          let statusColor = '';
          let statusText = '';

          switch (value) {
            case 'pending':
              statusColor =
                'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200';
              statusText = 'Pending';
              break;
            case 'approved':
              statusColor =
                'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
              statusText = 'Approved';
              break;
            case 'rejected':
              statusColor =
                'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
              statusText = 'Rejected';
              break;
            case 'revoked':
              statusColor =
                'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200';
              statusText = 'Revoked';
              break;
            case 'approved_by_admin':
              statusColor =
                'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
              statusText = 'Approved by Admin';
              break;
            case 'club_approval_pending':
              statusColor =
                'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
              statusText = 'Club Approval Pending';
              break;
            default:
              statusColor = 'bg-gray-100 text-gray-800';
              statusText = 'Unknown';
              break;
          }

          return (
            <div className="flex justify-start">
              <span
                className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${statusColor}`}
                role="status"
                aria-label={`Request status: ${statusText}`}
              >
                {statusText}
              </span>
            </div>
          );
        },
        meta: {
          className: 'min-w-[160px] w-[160px]',
        },
      },
      {
        accessor: (row: HarborAssociationRequest) => row.request_message,
        id: 'message',
        header: 'Request Message',
        enableSorting: false,
        cell: ({ row }: { row: { original: HarborAssociationRequest } }) => {
          const value = row.original.request_message;
          return (
            <div className="max-w-xs">
              {value ? (
                <span className="text-sm text-gray-600 truncate block">
                  {value.length > 50 ? `${value.substring(0, 50)}...` : value}
                </span>
              ) : (
                <span className="text-sm text-gray-400 italic">No message</span>
              )}
            </div>
          );
        },
      },
      {
        accessor: (row: HarborAssociationRequest) => row.created_at,
        id: 'date',
        header: 'Requested Date',
        enableSorting: false,
        cell: ({ row }: { row: { original: HarborAssociationRequest } }) => {
          const value = row.original.created_at;
          return (
            <span className="text-sm">{formatDisplayDate(value, 'N/A')}</span>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: HarborAssociationRequest } }) => (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleOpenDetailsModal(row.original)}
              className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="View Details"
            >
              <Eye className="w-4 h-4 text-blue-600" />
            </button>

            {/* Show actions based on status and permissions */}
            {hasPermission('approve_reject_harbor_approval_request') && (
              <>
                {/* For pending status - show approve and cancel buttons */}
                {row.original.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleOpenDetailsModal(row.original)}
                      disabled={isApproving}
                      className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
                      title="Approve Request"
                    >
                      <Check className="w-4 h-4 text-green-600" />
                    </button>

                    <button
                      onClick={() =>
                        handleCancelRequest(
                          row.original.id,
                          'reject',
                          'Request rejected by harbor admin'
                        )
                      }
                      disabled={isCancelling}
                      className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
                      title="Reject Request"
                    >
                      <Ban className="w-4 h-4 text-red-600" />
                    </button>
                  </>
                )}

                {/* For club_approval_pending status - no actions allowed */}
                {/* This status means waiting for yacht club admin approval */}

                {/* For approved status - show revoke button */}
                {row.original.status === 'approved' && (
                  <button
                    onClick={() => handleOpenDetailsModal(row.original)}
                    disabled={isRevoking}
                    className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
                    title="Revoke Association"
                  >
                    <RotateCcw className="w-4 h-4 text-orange-600" />
                  </button>
                )}
              </>
            )}
          </div>
        ),
      },
    ],
    [hasPermission, isApproving, isRejecting, isRevoking, isCancelling]
  );

  return (
    <div className="harbor-club-requests p-6 card shadow-md rounded-lg">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-semibold text-gray-800">
          Club Association Requests
        </h1>

        <div className="flex items-center space-x-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search clubs..."
              className="border dark:bg-gray-200 rounded-md py-2 px-3 pl-9 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 contrast:bg-transparent"
              value={searchTerm}
              onChange={handleSearch}
              aria-label="Search clubs"
            />
            <KeenIcon
              type="filled"
              icon="magnifier"
              className="w-4 h-4 absolute left-3 top-2.5 text-gray-400"
            />
          </div>

          <div className="relative">
            <select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className="select select-sm h-8 sm:h-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Filter by status"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="club_approval_pending">
                Club Approval Pending
              </option>
              <option value="approved">Approved</option>
              <option value="approved_by_admin">Approved by Admin</option>
              <option value="rejected">Rejected</option>
              <option value="revoked">Revoked</option>
            </select>
          </div>
        </div>
      </div>

      {/* Log the data right before passing to DataGrid */}
      {isRequestsLoading || isFetching ? (
        <div className="text-center py-4" role="status" aria-live="polite">
          Loading clubs...
        </div>
      ) : (
        <DataGrid
          columns={columns}
          data={processedData}
          loading={isRequestsLoading || isFetching}
          pagination={{
            page: pagination.pageIndex,
            size: pagination.pageSize,
            more: true,
            totalItems,
            totalPages,
            setPagination: (newPaginationState) => {
              setPagination(newPaginationState);
              setTimeout(() => {
                refetch();
              }, 10);
            },
            info: `{from} - {to} of {count}`,
            sizes: [5, 10, 25, 50, 100],
            sizesLabel: 'Show',
            sizesDescription: 'per page',
          }}
          sorting={[{ id: 'created_at', desc: true }]}
          serverSide={true}
          messages={{
            empty: 'No club requests found',
            loading: 'Loading club requests...',
          }}
        />
      )}

      {selectedRequest && (
        <HarborClubRequestDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={() => setIsDetailsModalOpen(false)}
          request={selectedRequest}
          onApprove={handleApproveRequest}
          onReject={handleRejectRequest}
          onRevoke={handleRevokeRequest}
          isApproving={isApproving}
          isRejecting={isRejecting}
          isRevoking={isRevoking}
        />
      )}
    </div>
  );
};

export default HarborClubRequests;
